import * as React from "react";

import { Combobox, Input } from "@/components/ui";
import { VideoProjectCoachConfig } from "@/interfaces";
import { VideoProjectServices } from "@/services";

import { BillingCycleOption, buildBillingCycleOptions } from "./billingCycleOptions";
import {
  COACH_VIDEO_PROJECT_DATE_PRESET_LABELS,
  COACH_VIDEO_PROJECT_DATE_PRESETS,
  CoachVideoProjectDatePreset,
  CreatedDateBounds,
  createdDateBoundsFromCycle,
  createdDateBoundsFromRange,
  initialCoachVideoProjectDateFilter,
  resolveCoachVideoProjectDatePreset,
  writeStoredCoachVideoProjectDateFilter,
} from "./coachVideoProjectDateRange";

interface CoachVideoProjectDateFilterProps {
  coachId?: number;
  onBoundsChange: (bounds: CreatedDateBounds) => void;
}

export function CoachVideoProjectDateFilter({ coachId, onBoundsChange }: CoachVideoProjectDateFilterProps) {
  const [initialFilter] = React.useState(initialCoachVideoProjectDateFilter);
  const [preset, setPreset] = React.useState<CoachVideoProjectDatePreset | null>(initialFilter.preset);
  const [startDate, setStartDate] = React.useState<string | null>(initialFilter.startDate);
  const [endDate, setEndDate] = React.useState<string | null>(initialFilter.endDate);
  const [billingCycleValue, setBillingCycleValue] = React.useState<string | null>(null);
  const [coachConfig, setCoachConfig] = React.useState<VideoProjectCoachConfig>();
  const [isConfigLoading, setIsConfigLoading] = React.useState(false);

  React.useEffect(() => {
    if (!coachId) return;
    let cancelled = false;
    setIsConfigLoading(true);
    VideoProjectServices.getCoachConfig(coachId)
      .then(config => {
        if (!cancelled) setCoachConfig(config);
      })
      .catch(error => {
        console.error("Failed to load coach config", error);
      })
      .finally(() => {
        if (!cancelled) setIsConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [coachId]);

  const billingCycleOptions = React.useMemo(() => {
    if (!coachConfig) return [];
    return buildBillingCycleOptions(coachConfig.newCycleDay || 1);
  }, [coachConfig]);

  React.useEffect(() => {
    if (preset !== "billing_period" || billingCycleValue || billingCycleOptions.length === 0) return;
    setBillingCycleValue(billingCycleOptions[0].value);
  }, [preset, billingCycleValue, billingCycleOptions]);

  React.useEffect(() => {
    writeStoredCoachVideoProjectDateFilter(preset, startDate, endDate);
  }, [preset, startDate, endDate]);

  React.useEffect(() => {
    if (preset === "billing_period") {
      if (!billingCycleValue) {
        if (!isConfigLoading && billingCycleOptions.length === 0) onBoundsChange({});
        return;
      }
      const cycle = billingCycleOptions.find(option => option.value === billingCycleValue);
      onBoundsChange(cycle ? createdDateBoundsFromCycle(cycle) : {});
      return;
    }

    if (!preset) {
      onBoundsChange({});
      return;
    }

    onBoundsChange(createdDateBoundsFromRange(startDate, endDate));
  }, [preset, startDate, endDate, billingCycleValue, billingCycleOptions, isConfigLoading, onBoundsChange]);

  const handlePresetChange = (value: string | null | undefined) => {
    const nextPreset = (value as CoachVideoProjectDatePreset | null | undefined) || null;
    if (!nextPreset) {
      setPreset(null);
      setStartDate(null);
      setEndDate(null);
      setBillingCycleValue(null);
      return;
    }

    if (nextPreset === "billing_period") {
      setPreset(nextPreset);
      setStartDate(null);
      setEndDate(null);
      setBillingCycleValue(billingCycleOptions[0]?.value ?? null);
      return;
    }

    const dates = resolveCoachVideoProjectDatePreset(nextPreset, { startDate, endDate });
    setPreset(nextPreset);
    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
    setBillingCycleValue(null);
  };

  const selectedCycle = billingCycleOptions.find(option => option.value === billingCycleValue) ?? null;

  return (
    <div className="flex gap-4 items-center flex-wrap">
      <Combobox
        className="max-w-80 min-w-50 bg-white"
        items={COACH_VIDEO_PROJECT_DATE_PRESETS}
        value={preset}
        onValueChange={handlePresetChange}
        itemToStringLabel={item => COACH_VIDEO_PROJECT_DATE_PRESET_LABELS[item as CoachVideoProjectDatePreset]}
        placeholder="All time"
        showClear
      />
      {preset === "billing_period" ? (
        <Combobox
          className="max-w-80 min-w-50 bg-white"
          items={billingCycleOptions}
          value={selectedCycle}
          onValueChange={(option: BillingCycleOption | null) => setBillingCycleValue(option?.value ?? null)}
          itemToStringLabel={(option: BillingCycleOption) => option.label}
          placeholder={isConfigLoading ? "Loading billing cycles" : "Billing cycle"}
          disabled={isConfigLoading || billingCycleOptions.length === 0}
        />
      ) : null}
      {preset === "custom" ? (
        <div className="flex gap-2 items-center">
          <Input
            type="date"
            aria-label="Start date"
            value={startDate ?? ""}
            onChange={event => setStartDate(event.target.value || null)}
            className="h-9 w-40 bg-white text-sm"
          />
          <Input
            type="date"
            aria-label="End date"
            value={endDate ?? ""}
            onChange={event => setEndDate(event.target.value || null)}
            className="h-9 w-40 bg-white text-sm"
          />
        </div>
      ) : null}
    </div>
  );
}
